import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { Banner } from "@/components/ui/banner"
import { allowsFollowUp, isFlatLength, isRestrictedToFlatLength } from "@/lib/appointment-type-rules"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { ServiceSelect } from "@/modules/patients/agenda/teleconsultation/booking/components/service-select"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { Service, ServiceSpecialty } from "@/types/__generated__/union-types"
import { UI_DISPLAY_LANGUAGES } from "@/types/UiDisplayLanguage"

const SERVICE_OPTIONS: Service[] = [
  "ADMINISTRATIVE_REQUEST",
  "KIDS_LINE",
  "CONSULTATION",
  "EMERGENCY_LINE",
  "MEDGATE_CONNECT",
  "PREVENTION_ASSESSMENT",
  "PSYCHOSOCIAL",
  "STUDY_CALL",
  "TELE_GENERAL_PRACTITIONER",
]

const SPECIALTY_OPTIONS: ServiceSpecialty[] = [
  "NONE",
  "DERMATOLOGY",
  "GYNAECOLOGY",
  "CARDIOLOGY",
  "LONG_COVID",
  "OTORHINOLARYNGOLOGY",
  "PEDIATRICS",
  "UROLOGY",
  "VACCINATION_AND_TRAVEL_ADVICE",
]

const ServiceSpecialtySection = ({
  mode,
  isRestrictedByAppBooking = false,
  onServiceChange,
}: {
  mode: TeleconsultationMode
  isRestrictedByAppBooking?: boolean
  onServiceChange?: (service: Service) => void
}) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const { control, setValue } = useFormContext<TeleconsultationForm>()
  const serviceWatch = useWatch({ control, name: "service" })
  const appointmentTypeWatch = useWatch({ control, name: "appointmentType" })

  const serviceMappings = getServiceMapping().serviceMappings.filter((service) =>
    service.isAllowedInCountries.includes(countryCode),
  )
  const disableMedgateConnect = mode.kind === "EDITING_APPOINTMENT" && !isMedgateConnect(mode.data.appointment.service)

  const isEditingAFlatLengthAppointment =
    mode.kind === "EDITING_APPOINTMENT" && isFlatLength(mode.data.appointment.appointmentType)

  const isBookedAsAFollowUp = mode.kind === "FOLLOWUP_FOR_PROGRESS_NOTE" || appointmentTypeWatch === "FOLLOW_UP"

  // A flat-length appointment is only ever created, and a follow-up needs a service that allows the follow-up type
  const isRefused = (service: Service) =>
    (mode.kind === "EDITING_APPOINTMENT" && isRestrictedToFlatLength(service)) ||
    (isBookedAsAFollowUp && !allowsFollowUp(service))

  // Sorted by the German label, so the order stays the same whatever the UI language is
  const serviceOptions = SERVICE_OPTIONS.filter((service) =>
    serviceMappings.map((mapping) => mapping.service).includes(service),
  )
    .map((e) => ({
      value: e,
      label: t(`scheduling.waitingRoom.service.${e}`),
      labelGerman: t(`scheduling.waitingRoom.service.${e}`, { lng: UI_DISPLAY_LANGUAGES.GERMAN }),
      disabled:
        (e === "KIDS_LINE" && patient.age.years > 18) ||
        (isMedgateConnect(e) && !!disableMedgateConnect) ||
        isRefused(e),
    }))
    .toSorted((e1, e2) => e1.labelGerman.localeCompare(e2.labelGerman))

  const specialtyOptions = SPECIALTY_OPTIONS.filter((specialty) =>
    serviceMappings
      .filter((mapping) => mapping.service === serviceWatch)
      .map((mapping) => mapping.specialty)
      .includes(specialty),
  )
    .map((e) => ({
      value: e,
      label: t(`scheduling.waitingRoom.serviceSpecialty.${e}`),
      labelGerman: t(`scheduling.waitingRoom.serviceSpecialty.${e}`, { lng: UI_DISPLAY_LANGUAGES.GERMAN }),
    }))
    .toSorted((e1, e2) => {
      if (e1.value === "NONE") {
        return -1
      } else if (e2.value === "NONE") {
        return 1
      } else {
        return e1.labelGerman.localeCompare(e2.labelGerman)
      }
    })

  const isSpecialtyHidden = specialtyOptions.length <= 1 || isMedgateConnect(serviceWatch)

  return (
    <>
      <Controller
        control={control}
        name="service"
        render={({ field: { value, onChange } }) => (
          <ServiceSelect
            selectedValue={{
              value: value ?? "CONSULTATION",
              label: t(`scheduling.waitingRoom.service.${value}`),
            }}
            options={serviceOptions}
            label={t("patient.agenda.service")}
            onSelect={(option) => {
              onChange(option.value)
              const service = serviceOptions.find((s) => s.value === option.value)
              if (service) onServiceChange?.(service.value)
              setValue("specialty", "NONE")
            }}
            testId="consultation-service"
            disabled={isRestrictedByAppBooking || isEditingAFlatLengthAppointment}
          />
        )}
      />
      {isRestrictedByAppBooking && (
        <Banner type="inline" variant="info" data-testid="consultation-service-app-booked-restriction">
          {t("patient.agenda.appBookedRestriction.service")}
        </Banner>
      )}
      {!isRestrictedByAppBooking && isEditingAFlatLengthAppointment && (
        <Banner type="inline" variant="info" data-testid="consultation-service-full-hour-restriction">
          {t("patient.agenda.fullHourRestriction.service")}
        </Banner>
      )}

      {!isSpecialtyHidden && (
        <>
          <Controller
            control={control}
            name="specialty"
            render={({ field: { onChange, value } }) => (
              <ServiceSelect
                selectedValue={{
                  value: value ?? "NONE",
                  label: t(`scheduling.waitingRoom.serviceSpecialty.${value}`),
                }}
                options={specialtyOptions}
                label={t("patient.agenda.serviceSpecialty")}
                onSelect={(option) => onChange(option.value)}
                testId="consultation-specialty"
                disabled={isRestrictedByAppBooking}
              />
            )}
          />
          {isRestrictedByAppBooking && (
            <Banner type="inline" variant="info" data-testid="consultation-specialty-app-booked-restriction">
              {t("patient.agenda.appBookedRestriction.specialty")}
            </Banner>
          )}
        </>
      )}
    </>
  )
}

export { ServiceSpecialtySection }
